// Copied from DealerFlow 360 (dealerflow360/web/src/shared/payments/useAcceptJs.ts); spec references are DF360's.
import { useCallback, useEffect, useState } from 'react';
import {
  ACCEPT_LOAD_FAILED,
  ACCEPT_RELOAD_REQUIRED,
  AcceptJsError,
  acceptEnvironmentBlocked,
  loadAcceptJs,
} from './acceptJs';

export type AcceptJsStatus = 'idle' | 'loading' | 'ready' | 'failed' | 'blocked';

export interface AcceptJsState {
  status: AcceptJsStatus;
  /** Why the card can't be sent: R24a's or R22's sentence. */
  error: string | null;
  /** Loads the script again after it `failed`; a `blocked` page needs a reload instead. */
  retry: () => void;
}

/**
 * Loads Accept.js from the account's script URL while `enabled` (a charge workspace is open), at
 * most once per page session (R22, R24a). `blocked` means this page already ran another
 * environment's script: card entry waits for a reload. A load that `failed` can be tried again.
 */
export function useAcceptJs(scriptUrl: string | null | undefined, enabled: boolean): AcceptJsState {
  const [state, setState] = useState<Omit<AcceptJsState, 'retry'> & { url: string | null }>({ status: 'idle', error: null, url: null });
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  useEffect(() => {
    if (!enabled || !scriptUrl) return;
    if (acceptEnvironmentBlocked(scriptUrl)) {
      setState({ status: 'blocked', error: ACCEPT_RELOAD_REQUIRED, url: scriptUrl });
      return;
    }
    let current = true;
    setState({ status: 'loading', error: null, url: scriptUrl });
    loadAcceptJs(scriptUrl).then(
      () => {
        if (current) setState({ status: 'ready', error: null, url: scriptUrl });
      },
      (error: unknown) => {
        if (!current) return;
        const message = error instanceof AcceptJsError ? error.message : ACCEPT_LOAD_FAILED;
        setState({ status: message === ACCEPT_RELOAD_REQUIRED ? 'blocked' : 'failed', error: message, url: scriptUrl });
      },
    );
    return () => {
      current = false;
    };
  }, [enabled, scriptUrl, attempt]);

  if (!enabled || !scriptUrl || state.url !== scriptUrl) return { status: 'idle', error: null, retry };
  return { status: state.status, error: state.error, retry };
}
